import React, { useState } from 'react';

// components
import Avatar from 'components/Avatar';
import Button from 'components/UiKit/Button';
import TextField from 'components/UiKit/FieldText';
import FieldSelect from 'components/UiKit/FieldSelect';
import FieldPhone from 'components/UiKit/FieldPhone';

// Lib
import * as yup from 'yup';
import { shallowEqual } from 'react-redux';
import { patternRxpCheckEng } from 'lib/helpers';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { useAppDispatch, useAppSelector } from 'lib/hooks/useDispatch';

// Constants
import { codesFlag } from 'constans/code-flag';
import { styleFieldSelect } from 'constans/checkout';

// Actions
import { openModal } from 'reducers/modal';
import { deleteAvatarUser, updateProfile } from 'pages/Account/reducer';

// Type
type TypePropsFindObjectSelect = {
	[index: string]: string | number | boolean
}[];

// Schema
const schema = yup.object({
	lastName: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	position: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	firstName: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	companyName: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').required(),
	email: yup.string().matches(patternRxpCheckEng, 'Use Latin symbols only.').email(),
	title: yup.string().required('Title is required!'),
	phone: yup.string()
}).required();
type FormData = yup.InferType<typeof schema>;

export const Profile: React.FC = () => {
	const dispatch = useAppDispatch();

	const {
		avatar,
		loading,
		settings,
		optionsTitle,
	} = useAppSelector((store) => {
		return {
			avatar: store.account.avatar,
			loading: store.account.loadingProfile,
			optionsTitle: store.auth.optionsTitle,

			settings: store.settings.user,
		};
	}, shallowEqual);

	const findObjectSelect = (data: TypePropsFindObjectSelect, currentValue: string, key: string) => data.length
		? data?.find((option) => option[key] === currentValue)
		: {};

	const defaultValues: { [index: string]: string | number } = {
		phone: settings.phone || '',
		title: settings?.title || '',
		email: settings?.email || '',
		position: settings?.position || '',
		lastName: settings?.lastName || '',
		firstName: settings?.firstName || '',
		companyName: settings?.organization || ''
	};

	const [disabledForm, setDisabledForm] = useState<boolean>(true);

	const {
		control,
		register,
		handleSubmit,
		formState: { errors }
	} = useForm<FormData>({
		resolver: yupResolver(schema),
		defaultValues
	});

	const handlerChangeDisabled = () => {
		setDisabledForm(!disabledForm);
	};

	const onSubmit: SubmitHandler<FormData> = (data: FormData) => {
		dispatch(updateProfile(data));
		setDisabledForm(true);
	};

	const handlerDeleteAvatar = () => {
		dispatch(deleteAvatarUser());
	};

	const handlerConfirmDeleteAvatar = () => {
		dispatch(openModal({
			color: 'error',
			type: 'confirm',
			actionSubmit: handlerDeleteAvatar,
			text: `Do you want to delete avatar?`
		}));
	};

	const onSelectFile = () => {
		dispatch(openModal({
			type: 'crop'
		}));
	};

	const name = settings?.firstName &&
		(`${settings?.firstName[0]}.` || '');

	return (
		<div className="account__profile">
			<div className="flex flex-ai-center flex-jc-between mb20">
				<div className="flex flex-ai-center">
					<h4 className="account__profile-title pl10">
						Your avatar
					</h4>
					<Button
						isSvg
						variable="icon"
						color="error"
						icon="deleteSvg"
						className="ml10"
						title="Delete avatar"
						disabled={disabledForm}
						onPress={handlerConfirmDeleteAvatar}
					/>
				</div>
				<Button
					isSvg
					icon="editSvg"
					color="error"
					variable="icon"
					title="Edit Profile info"
					onPress={handlerChangeDisabled}
				/>
			</div>
			<div className="pl10 mb30 flex flex flex-ai-center">
				<Avatar avatar={avatar} name={name} />
				<div className="ml15">
					<p className="account__upload-photo-text mb10">
						Upload new photo
					</p>
					<Button
						color="green"
						variable="outline"
						title="Add image"
						label="Upload image"
						onPress={onSelectFile}
						disabled={disabledForm}
					/>
				</div>
			</div>

			<form onSubmit={handleSubmit(onSubmit)}>
				<p className="account__form-text mb20 pl10">Personal info</p>

				<div className="account__form-row flex flex-ai-center flex-direction-column-desktop mb20">
					<TextField
						required
						name="firstName"
						label="First name *"
						disabled={disabledForm}
						className="account__col"
						placeholder="Your answer"
						isError={!!errors?.firstName}
						register={register('firstName')}
						msgError={errors?.firstName ? errors?.firstName.message : ''}
					/>
					<TextField
						required
						name="lastName"
						label="Last name *"
						disabled={disabledForm}
						className="account__col"
						placeholder="Your answer"
						isError={!!errors?.lastName}
						register={register('lastName')}
						msgError={errors?.lastName ? errors?.lastName.message : ''}
					/>
				</div>

				<div className="account__form-row flex flex-ai-center flex-direction-column-desktop mb20">
					<Controller
						name="title"
						control={control}
						render={({ field: { onChange, value } }) => {
							const findObject = findObjectSelect(optionsTitle, value, 'value');
							return (
								<FieldSelect
									required
									name="title"
									label="Title *"
									placeholder="Select"
									options={optionsTitle}
									disabled={disabledForm}
									className="account__col"
									styles={styleFieldSelect}
									isError={!!errors?.title}
									onChange={(val: any) => onChange(val.value)}
									value={Object?.values(value)?.length ? findObject : []}
									msgError={errors?.title ? errors?.title?.message : ''}
									register={register('title', { required: true })}
								/>
							);
						}}
					/>

					<TextField
						required
						name="position"
						label="Position *"
						disabled={disabledForm}
						className="account__col"
						placeholder="Your answer"
						isError={!!errors?.position}
						register={register('position')}
						msgError={errors?.position ? errors?.position.message : ''}
					/>
				</div>

				<TextField
					required
					name="companyName"
					label="Company name *"
					disabled={disabledForm}
					placeholder="Your answer"
					className="full-width mb20"
					isError={!!errors?.companyName}
					register={register('companyName')}
					msgError={errors?.companyName ? errors?.companyName.message : ''}
				/>

				<div className="account__form-row flex flex-ai-center flex-direction-column-desktop mb50">
					<TextField
						required
						disabled
						name="email"
						label="Email address *"
						className="account__col"
						isError={!!errors?.email}
						placeholder="yourcompany@email.com"
						msgError={errors.email ? errors.email.message : ''}
						register={register('email', { required: 'Email is required', pattern: /^\S+@\S+$/i })}
					/>

					<Controller
						name="phone"
						control={control}
						rules={{ required: false }}
						render={({ field: { onChange, value } }) => (
							<FieldPhone
								big
								required
								name="phone"
								maskChar="X"
								value={value}
								countryCode="UA"
								options={codesFlag}
								placeholderChar="X"
								label="Phone number *"
								disabled={disabledForm}
								className="account__col"
								style={{ width: '100%' }}
								onChange={(name: string, value: string) => onChange(value)}
								register={register('phone', { required: false, pattern: /^\S+@\S+$/i })}
							/>
						)}
					/>
				</div>

				<Button
					type="submit"
					variable="info"
					loading={loading}
					label="Update & Save"
					disabled={disabledForm}
					className="centered-button"
				/>
			</form>
		</div>
	);
};

export default Profile;
